/**
 * Client for the document-role catalogue — `/administration/document-analysis/*` on tri-server,
 * platform administrators only.
 *
 * The catalogue is append-only: a save writes the next generation of a role and closes the
 * previous one, and a deletion retires a role without touching what other roles say about it. None
 * of that is the client's business except in one respect — the reply to a save is the generation
 * that was written, so the panel shows what is now on record rather than what it sent.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

/** A term of the catalogue and the ontology class it maps to, where one is known. */
export interface DocumentEntity {
  term: string;
  className: string | null;
  mappingNote?: string;
}

export interface DocumentRelation {
  sourceTerm: string;
  sourceClassName: string | null;
  name: string;
  targetTerm: string;
  targetClassName: string | null;
}

/** What a subtype has to say about how it is recognised. */
export interface DocumentSubtypeClassification {
  description?: string;
  cues?: string[];
}

export interface DocumentSubtype {
  name: string;
  kind: "param" | "parameter" | "operator" | string;
  stateOperator?: "create" | "amend" | "terminate" | "attest" | string;
  /** The subtype changes the state as of an earlier date, not the date it carries. */
  exTunc?: boolean;
  note?: string;
  classification?: DocumentSubtypeClassification;
}

/** Two roles that look alike, and the sentence that tells them apart. */
export interface DocumentConfusable {
  role: string;
  discriminator: string;
}

/**
 * What the classifier is told about a role.
 *
 * Not documentation: this is the prompt. A description that is vague, a cue that names a phrase no
 * document carries, a missing discriminator against the type it is confused with — each of those
 * shows up directly as a misclassification, which is why it is edited here and not in a file.
 */
export interface DocumentClassification {
  description?: string;
  /** Phrases that speak for this role. */
  positiveCues?: string[];
  /** How the document is laid out, as against what it says. */
  structureCues?: string[];
  confusableWith?: DocumentConfusable[];
}

export interface DocumentBackRef {
  targetRole: string;
  keySharpness: "amtlich" | "formal" | "frei" | string;
  note?: string;
}

export interface DocumentExpectationEdge {
  targetRole: string;
  trigger?: string;
  cadenceSource?: string;
}

export interface DocumentRefCode {
  source: string;
  code: string | null;
  status: "unverified" | "verified" | string;
}

export interface DocumentRole {
  _id?: string;
  name: string;
  category: string;
  classification?: DocumentClassification;
  refCodes?: DocumentRefCode[];
  subtypes?: DocumentSubtype[];
  stateOperatorDefault?: { stateOperator: string; note?: string } | null;
  backRefs?: DocumentBackRef[];
  expectationEdges?: DocumentExpectationEdge[];
  primaryEntities?: DocumentEntity[];
  referenceEntities?: DocumentEntity[];
  relations?: DocumentRelation[];
  keyNote?: string;
  openWorldNote?: string;
  clusterRole?: string;
  active?: boolean;
  generation?: number;
  provenance?: Record<string, any>;
}

/** Who would be left pointing at a role that is retired. */
export interface DocumentRoleReference {
  name: string;
  via: "backRefs" | "expectationEdges" | string;
}

/* ------------------------------------------------------------------ */
/* The processing test                                                 */
/* ------------------------------------------------------------------ */

export type AnalysisStep =
  | "conversion"
  | "roleClassification"
  | "subtypeClassification"
  | "objectExtraction"
  | "matching"
  | "job";

export type AnalysisStatus = "started" | "progress" | "completed" | "failed" | "done" | "error";

export interface AnalysisEvent {
  step: AnalysisStep;
  status: AnalysisStatus;
  progress?: number;
  message?: string;
  payload?: any;
  elapsedMs: number;
}

export interface ConversionPayload {
  format: "markdown" | "rows";
  content: string;
  truncated?: boolean;
  meta: { pages?: number; sheets?: number; chars: number };
}

export interface RolePayload {
  role: string | null;
  confidence: number;
  alternatives: Array<{ role: string; confidence: number }>;
  rationale: string;
  undecided?: boolean;
  undecidedReason?: string;
}

export interface SubtypePayload {
  subtype: string | null;
  parameterValue: string | null;
  confidence: number;
  rationale: string;
  skipped?: boolean;
  reason?: string;
}

/** One object the document describes, as the extraction found it. */
export interface ExtractedInstance {
  term: string;
  className: string | null;
  /** Keys of the tri-model schema; a declared attribute the document did not mention is null. */
  attributes: Record<string, any>;
  /** What the document said that the schema has no room for. */
  unmappedAttributes?: Record<string, any>;
  sourceRef: { quote: string; location: string };
  confidence: number;
}

export interface ExtractionReport {
  instanceCount: number;
  byTerm: Record<string, number>;
  movedToUnmapped: number;
  typeViolations: number;
  droppedInstances: number;
  unknownClasses?: string[];
  /** Katalogbegriffe, die das Modell längst als Feld führt — je als „Begriff → Klasse.feld". */
  coveredTerms?: string[];
}

/* ------------------------------------------------------------------ */
/* The record of the runs                                              */
/* ------------------------------------------------------------------ */

export type AnalysisRunStatus = "running" | "completed" | "failed" | "aborted" | "undecided";

/** A row of the analyses table: the run without its arrays, and their sizes instead. */
/** Der Ordner eines Projekts in der Ablage, wie ihn der Server auflöst. */
export interface ProjectFolder {
  /** Pfad im Volume des Mandanten, mit abschließendem Schrägstrich. */
  path: string;
  /** `<Name> (<Id>)`. */
  name: string;
  created: boolean;
  renamed: boolean;
}

export interface AnalysisRow {
  _id: string;
  jobId: string;
  startedAt: string;
  finishedAt: string | null;
  status: AnalysisRunStatus;
  failedStep: string | null;
  /** Warum er scheiterte, in einem Satz — `failedStep` nennt die Stelle, nicht den Grund. */
  failureMessage?: string | null;

  roleName: string | null;
  roleConfidence: number | null;
  subtypeName: string | null;
  parameterValue: string | null;
  subtypeConfidence: number | null;

  document: {
    documentId: string;
    fileName: string;
    contentHash: string;
    convertedFormat: string | null;
    charCount: number | null;
    /**
     * Die Datei in der Ablage — und damit der Schlüssel, unter dem der Dateimanager Läufe findet.
     *
     * Nicht dasselbe wie `documentId`: Das ist der Satz über den *Inhalt*, unter dem derselbe
     * Vertrag über mehrere Uploads hinweg dasselbe Dokument bleibt. Wer eine Zeile des Dateibaums
     * einem Lauf zuordnen will, braucht diese hier.
     */
    managedFileId?: string | null;
  };

  extractedCount: number;
  boundCount: number;

  dataRoomId: string | null;
  provenance?: { roleGeneration?: number | null; llmModel?: string; createdAt?: string };

  /**
   * Ob für diesen Lauf ein Plan bereitsteht — ohne den Plan selbst.
   *
   * `awaiting` heisst: gelesen, gerechnet, noch nichts geschrieben. `notOffered` steht an einem
   * Verarbeitungstest, dessen Plan zwar gebaut wurde, aber nie angewendet werden darf.
   */
  mergeStatus?: "awaiting" | "applying" | "applied" | "notOffered" | null;
  mergeSummary?: MatchingSummary | null;
}

/** Was das Anwenden tatsächlich getan hat. */
export interface MergeReport {
  updated: number;
  created: number;
  referenced: number;
  confirmed: number;
  skipped: number;
  failed: number;
  operations: Array<{
    op: PlanOperation["op"];
    className?: string;
    objectId?: string;
    outcome: "applied" | "confirmed" | "skipped" | "failed";
    reason?: string;
  }>;
}

/** One staged object, as the detail view reads it back. */
export interface StoredExtractedObject {
  _id: string;
  analysisId: string;
  term: string;
  className: string | null;
  attributes: Record<string, any>;
  unmappedAttributes?: Record<string, any>;
  sourceRef: { quote: string; location: string };
  confidence: number;
  resolution?: {
    status: "unresolved" | "ambiguous" | "resolved" | "new" | "rejected";
    candidates: any[];
    boundObjectId: string | null;
    boundBy: string | null;
    boundAt: string | null;
  };
}

/**
 * Woran ein Dokument gebunden wurde, in Worten.
 *
 * Kein Klassenbezeichner und keine Kennung: Die Frage im Datenraum ist „worüber redet dieses
 * Dokument?", und darauf ist „Lease" keine Antwort. Der Server übersetzt über die Beschriftungen
 * des Modells; hier wird nur noch gezeichnet.
 */
export interface BoundObjectView {
  /** Wie das Dokument die Sache nennt. */
  term: string;
  /** Die Art — „Mietvertrag". */
  classLabel: string;
  /** Woran gebunden wurde — „Wohnung 12 · 3. OG". */
  designation: string | null;
  /** Die identifizierenden Werte, beschriftet. */
  facts: Array<{ label: string; value: string }>;
  objectId: string;
  boundBy: string | null;
  boundAt: string | null;
  /** Das Objekt gibt es nicht mehr — die Bindung zeigt ins Leere. */
  missing: boolean;
}

/**
 * Ein Gebäude, das ein Dokument nennt und das Projekt noch nicht kennt.
 *
 * Der Gedanke: Ein Datenraum soll sein Projekt aufbauen können. Wer Verträge hineinzieht, hat damit
 * schon gesagt, um welche Häuser es geht — nur steht das bisher allein in den Befunden und nirgends
 * in den Stammdaten.
 */
export interface SiteProposal {
  buildingId: string;
  /** Wie das Dokument die Sache nannte — damit die Frage an einen Beleg gebunden bleibt. */
  term: string;
  address: {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  };
  /** Die Anschrift in einer Zeile, in der Schreibweise des Modells. */
  label: string;
}

/** A class that exists and a property it lacks — the cure is a field. */
export interface AttributeGap {
  className: string;
  attribute: string;
  occurrences: number;
  /** In how many separate runs: one document repeating itself is not several agreeing. */
  analyses: number;
  terms: string[];
  roles: string[];
  examples: string[];
  /**
   * Wofür das Feld steht — die Sätze aus den Läufen, neueste zuerst.
   *
   * Mehrere und nicht einer: Neun Verträge formulieren dieselbe Sache neun Mal anders, und die
   * Beschreibung, die später am Feld im Modell steht, soll mit den Belegen vor Augen gewählt
   * werden.
   */
  descriptions: string[];
  lastSeen: string | null;
}

/** A term with no class at all — the cure is a class, or a place on an existing one. */
export interface ClassGap {
  term: string;
  occurrences: number;
  analyses: number;
  roles: string[];
  /** The field names the extraction chose for itself. */
  attributes: string[];
  examples: string[];
  /** Was diese Sache ist — die Sätze aus den Läufen, neueste zuerst. */
  descriptions: string[];
  lastSeen: string | null;
}

export interface DataRoom {
  _id: string;
  name: string;
  /** Set when the room is a project's own — the dashboard resolves it by project. */
  projectId?: string;
  createdAt?: string;
}

/** Everything the analyses table can be narrowed by. */
export interface AnalysisFilter {
  q?: string;
  roleName?: string;
  subtypeName?: string;
  status?: string[];
  dataRoomId?: string;
  contentHash?: string;
  boundObjectId?: string;
  from?: string;
  to?: string;
  sort?: string;
  direction?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  limit?: number;
}

/** One candidate the estate offers for an extracted object. */
export interface ResolutionCandidate {
  objectId: string;
  className: string;
  /** 1 = the same thing, 0 = unrelated. */
  score: number;
  keyName: string;
  sharpness: string;
  matchedOn: string[];
  constraintViolations: string[];
  /** Enough of the stored object to recognise it. */
  label: string;
  /** The filled declared fields — what a table of candidates is built from. */
  values?: Record<string, any>;
}

export interface ResolutionNode {
  extractedObjectId: string;
  term: string;
  className: string | null;
  status: "resolved" | "ambiguous" | "new" | "unresolved" | string;
  reason?: string;
  boundObjectId: string | null;
  boundClassName: string | null;
  boundBy: "auto" | "user" | null;
  candidateCount: number;
  candidates: ResolutionCandidate[];
  /** Keys that could not be evaluated, and why — the explanation behind a bare "new". */
  skippedKeys: string[];
  /** The key that found the candidates and the values it compared. */
  searchedKey?: string | null;
  searchedValues?: Record<string, any> | null;
}

export interface PlanOperation {
  op: "updateAttributes" | "createObject" | "createReference" | "confirmReference" | "unresolved";
  extractedObjectId?: string;
  target?: { className: string; id: any };
  source?: { className: string; id: any };
  className?: string;
  name?: string;
  changes?: Array<{ field: string; old: any; new: any }>;
  attributes?: Record<string, any>;
  references?: Array<{ name: string; target: { className: string; id: any } }>;
  confidence?: number;
  requiresReview?: boolean;
  reason?: string;
  candidates?: Array<{ objectId: any; className: string; score: number; matchedOn: string[] }>;
  /**
   * Was dieser Neuanlage fehlen würde, das das Modell für verpflichtend hält.
   *
   * Ist an irgendeiner Neuanlage etwas gesetzt, wird der ganze Plan nicht übernommen — ein Plan
   * wird genau einmal angewendet, und eine übersprungene Neuanlage wäre endgültig weg. Der Dialog
   * sperrt deshalb, statt teilweise zu schreiben.
   */
  incomplete?: Array<{ kind: "field" | "relationship"; name: string; label: string }>;
  /** Bei einem Listendokument: die Zeile(n), aus denen die Operation stammt. */
  rows?: number[];
  /** Bei einem Dokumentenbündel: der Abschnitt, aus dem sie stammt. */
  section?: string;
}

/** Ein Abschnitt eines Dokumentenbündels, wie der Lauf ihn gelesen hat. */
export interface AnalysisSection {
  number: string;
  title: string;
  pageFrom: number;
  pageTo: number;
  roleName: string | null;
  subtypeName?: string | null;
  objectCount: number;
}

export interface MatchingSummary {
  resolved: number;
  ambiguous: number;
  new: number;
  updates: number;
  creates: number;
  reviewRequired: number;
}

export interface MatchingPayload {
  summary?: MatchingSummary;
  operations?: PlanOperation[];
  perNode?: ResolutionNode[];
  config?: Record<string, any>;
  skipped?: boolean;
  reason?: string;
}

export interface ExtractionPayload {
  instances?: ExtractedInstance[];
  report?: ExtractionReport;
  skipped?: boolean;
  reason?: string;
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.error || errorData.message || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

export const useDocumentAnalysisAPI = () => {
  const baseUrl = `${configuration.serverUrl}/administration/document-analysis`;

  const request = async (method: string, path: string, body?: any) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    return processResponse(response);
  };

  return {
    getDocumentRoles: (): Promise<{ roles: DocumentRole[] }> => request("GET", "/document-roles"),

    createDocumentRole: (role: { name: string; category: string }): Promise<{ role: DocumentRole }> =>
      request("POST", "/document-roles", role),

    /** Writes the next generation. What comes back is that generation, not what was sent. */
    saveDocumentRole: (name: string, changes: Partial<DocumentRole>): Promise<{ role: DocumentRole }> =>
      request("PUT", `/document-roles/${encodeURIComponent(name)}`, changes),

    /** Retires the role. The references other roles hold on it stay and are reported back. */
    retireDocumentRole: (name: string): Promise<{ retired: string; dangling: DocumentRoleReference[] }> =>
      request("DELETE", `/document-roles/${encodeURIComponent(name)}`),

    getDocumentRoleReferences: (name: string): Promise<{ references: DocumentRoleReference[] }> =>
      request("GET", `/document-roles/${encodeURIComponent(name)}/references`),

    /** Start a run. Comes back as soon as there is something to watch, not when it is done. */
    startAnalysisJob: async (
      file: File,
      dataRoomId?: string | null,
      /**
       * Der Ordner, in dem die Datei landen soll — der, auf den gezogen wurde.
       *
       * Der Server prüft ihn gegen den Projektordner und verwirft ihn, wenn er nicht darin liegt.
       * Ohne Angabe landet die Datei im Projektordner selbst.
       */
      folderPath?: string | null,
      /**
       * Woher der Lauf kommt — und damit, ob sein Plan je angewendet werden darf.
       *
       * Der Server erschliesst das nicht aus dem Datenraum: Auch der Verarbeitungstest kann einem
       * zugeordnet werden, und dort ist ein Lauf zum Ansehen da. Nur der Dateimanager sagt hier
       * ausdrücklich `dataRoom`.
       */
      origin?: "dataRoom" | "test",
      // The record's id comes back with the job: the row can be shown before the first event.
    ): Promise<{ jobId: string; analysisId: string | null }> => {
      const body = new FormData();
      body.append("file", file);
      if (dataRoomId) body.append("dataRoomId", dataRoomId);
      if (folderPath) body.append("folderPath", folderPath);
      if (origin) body.append("origin", origin);

      // Not through `request`: a multipart body must not carry a JSON content type, and
      // `authenticatedFetch` already knows to leave FormData alone.
      return processResponse(await authenticatedFetch(`${baseUrl}/jobs`, { method: "POST", body }));
    },

    /** Cancel a run. What has been started is not un-started; nothing further happens. */
    abortAnalysisJob: (jobId: string): Promise<{ aborted: boolean }> =>
      request("DELETE", `/jobs/${encodeURIComponent(jobId)}`),

    /** The record of the runs. Rows carry counts, never the arrays. */
    getAnalyses: (filter: AnalysisFilter = {}): Promise<{ analyses: AnalysisRow[]; total: number }> =>
      request("GET", `/analyses${analysisQuery(filter)}`),

    /** One run with the objects it staged. */
    getAnalysis: (
      id: string,
    ): Promise<{
      analysis: AnalysisRow & {
        origin?: string | null;
        matchingPlan?: { operations: PlanOperation[]; summary: MatchingSummary; status: string } | null;
        merge?: (MergeReport & { appliedAt?: string }) | null;
        /** Bei einem Dokumentenbündel: seine Abschnitte, wie der Lauf sie gelesen hat. */
        sections?: AnalysisSection[];
      };
      extractedObjects: StoredExtractedObject[];
    }> => request("GET", `/analyses/${encodeURIComponent(id)}`),

    /**
     * Eine Katalogfrage beantworten — und damit die Sperre auflösen, die sie erzeugt hat.
     *
     * Der Server nimmt nur einen Wert an, der unter den Kandidaten stand: Sonst wäre das ein Weg,
     * ein beliebiges Wort als Katalogschlüssel in den Bestand zu schreiben, und die Sperre verlöre
     * ihren Sinn.
     */
    chooseVocabulary: (
      analysisId: string,
      choice: { extractedObjectId: string; field: string; value: string },
    ): Promise<{ chosen: boolean }> =>
      request("POST", `/analyses/${encodeURIComponent(analysisId)}/vocabulary`, choice),

    /**
     * Den Plan anwenden — der einzige Aufruf im Portal, der den Bestand verändert.
     *
     * `confirmed` steht im Rumpf und nicht im Pfad: Der Server soll eine Anfrage abweisen können,
     * die versehentlich entsteht, und ein Pfad allein sagt nichts darüber, ob jemand zugestimmt hat.
     */
    mergeAnalysis: (analysisId: string): Promise<{ merged: boolean; report: MergeReport }> =>
      request("POST", `/analyses/${encodeURIComponent(analysisId)}/merge`, { confirmed: true }),

    getDataRooms: (): Promise<{ dataRooms: DataRoom[] }> => request("GET", "/data-rooms"),

    /** Where the documents said more than the model can hold. A query; nothing is written for it. */
    getGaps: (filter: {
      q?: string;
      minOccurrences?: number;
      roleName?: string;
      dataRoomId?: string;
    } = {}): Promise<{ attributeGaps: AttributeGap[]; classGaps: ClassGap[] }> =>
      request("GET", `/gaps${analysisQuery(filter as any)}`),

    /** A person chooses one of the candidates. Writes the reading of the document, not the estate. */
    bindExtractedObject: (
      extractedObjectId: string,
      candidateObjectId: string,
      className: string | null,
    ): Promise<{ resolution: any }> =>
      request("POST", `/extracted-objects/${encodeURIComponent(extractedObjectId)}/bind`, {
        candidateObjectId,
        className,
      }),

    /** None of them: the estate does not hold this thing, which is what `new` means. */
    rejectExtractedObject: (extractedObjectId: string): Promise<{ resolution: any }> =>
      request("POST", `/extracted-objects/${encodeURIComponent(extractedObjectId)}/reject`),

    /** The staged objects of a run with what is concluded about each. */
    getResolution: (analysisId: string): Promise<{ extractedObjects: any[] }> =>
      request("GET", `/analyses/${encodeURIComponent(analysisId)}/resolution`),

    /**
     * Woran der Lauf gebunden hat — fachlich benannt.
     *
     * Getrennt von der Analyse selbst, weil es die gebundenen Objekte im Bestand nachliest: Wer nur
     * den Satz braucht, soll das nicht bezahlen.
     */
    getBindings: (analysisId: string): Promise<{ bindings: BoundObjectView[] }> =>
      request("GET", `/analyses/${encodeURIComponent(analysisId)}/bindings`),

    /** Gebäude dieses Laufs, die das Projekt noch nicht als Liegenschaft führt. */
    getSiteProposals: (analysisId: string): Promise<{ proposals: SiteProposal[] }> =>
      request("GET", `/analyses/${encodeURIComponent(analysisId)}/site-proposals`),

    /** Die Zusage: Liegenschaft anlegen oder finden und dem Projekt zuordnen. */
    acceptSiteProposal: (
      analysisId: string,
      buildingId: string,
    ): Promise<{ siteId: string; created: boolean }> =>
      request("POST", `/analyses/${encodeURIComponent(analysisId)}/site-proposals`, { buildingId }),

    /**
     * Diese Anschrift gehört nicht zum Projekt — und danach soll nicht wieder gefragt werden.
     *
     * Gemerkt wird am Datenraum, nicht im Browser: Die Absage ist eine Aussage über das Projekt.
     */
    declineSiteProposal: (analysisId: string, buildingId: string): Promise<{ declined: boolean }> =>
      request("POST", `/analyses/${encodeURIComponent(analysisId)}/site-proposals/decline`, {
        buildingId,
      }),

    /** The data room of a project, created on first use. */
    /**
     * Der Datenraum eines Projekts — und sein Ordner in der Ablage.
     *
     * Zwei Dinge in einer Antwort, weil sie zusammen gebraucht werden: `dataRoom` sammelt die
     * Analysen, `folder` sagt, wo die Dateien liegen. Die Ansicht listet den Ordner und legt die
     * Analysen darüber.
     *
     * `folder` fehlt, wo die Ablage nicht erreichbar war. Dann bleiben die Analysen lesbar und die
     * Ordneransicht aus — der Aufrufer muss den Fall abfangen.
     */
    resolveProjectDataRoom: (
      projectId: string,
      name: string,
    ): Promise<{ dataRoom: DataRoom; folder: ProjectFolder | null }> =>
      request("POST", "/data-rooms/for-project", { projectId, name }),

    /**
    /**
     * Die Analysen zu einer Menge von Dateien — die Überlagerung über den Dateibaum.
     *
     * Über die Id der **abgelegten Datei**, nicht über die des `Document`. Die beiden sind
     * verschieden: `Document` entsteht aus dem Inhalts-Hash und kennt die Ablage nicht. Wer vom
     * Dateibaum kommt, hat Datei-Ids in der Hand.
     *
     * Eine Antwort je Id, und nur für die, zu denen es etwas gibt. Eine Datei ohne Eintrag ist eine
     * Datei ohne Analyse: keine Störung, sondern der Normalfall für alles, was jemand über den
     * Dateimanager abgelegt hat.
     */
    analysesByFiles: (fileIds: string[]): Promise<{ analyses: Record<string, AnalysisRow> }> =>
      request("POST", "/analyses/by-documents", { documentIds: fileIds }),

    /** Remove a run and what it staged. Asked for in the interface before it is called. */
    deleteAnalysis: (id: string): Promise<{ deleted: boolean }> =>
      request("DELETE", `/analyses/${encodeURIComponent(id)}`),

    /**
     * Follow a run.
     *
     * Over `fetch` rather than `EventSource`, because the endpoint is authenticated and
     * `EventSource` cannot set a header — the alternative would be the token in the query string,
     * which is the one place a credential must never be. The wire format is ordinary SSE, so the
     * server side stays standard: `Last-Event-ID` says where to resume, and the `id:` lines are
     * what makes that possible.
     */
    streamAnalysisJob: async (
      jobId: string,
      lastEventId: number,
      onEvent: (event: AnalysisEvent, id: number) => void,
      signal: AbortSignal,
    ): Promise<void> => {
      const response = await authenticatedFetch(`${baseUrl}/jobs/${encodeURIComponent(jobId)}/events`, {
        method: "GET",
        headers: { Accept: "text/event-stream", "Last-Event-ID": String(lastEventId) },
        signal,
      });

      // `authenticatedFetch` answers with nothing when the session is gone — it has already sent the
      // browser to the login page. Saying so beats reading `.ok` off undefined.
      if (!response) throw new UnauthorizedError();

      if (!response.ok) {
        await processResponse(response);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new ApplicationError("Der Ereignisstrom lieferte keinen Inhalt.");

      const decoder = new TextDecoder();
      let buffer = "";

      // Read the body in whatever chunks arrive and cut it into SSE frames at the blank line. A
      // frame can straddle two chunks, which is the whole reason for the buffer.
      for (;;) {
        const { done, value } = await reader.read();
        if (done) return;

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");

          const parsed = parseFrame(frame);
          if (parsed) onEvent(parsed.event, parsed.id);
        }
      }
    },
  };
};

/**
 * The filter as a query string.
 *
 * Only what is set: an empty parameter would filter on the empty string, which matches nothing —
 * an easy way to build a table that is silently always empty.
 */
export const analysisQuery = (filter: AnalysisFilter): string => {
  const parameters = new URLSearchParams();

  Object.keys(filter).forEach((key) => {
    const value = (filter as any)[key];

    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length > 0) parameters.set(key, value.join(","));
      return;
    }

    parameters.set(key, String(value));
  });

  const query = parameters.toString();

  return query ? `?${query}` : "";
};

/**
 * One SSE frame: the `id:` line and the `data:` line.
 *
 * Comment frames (`: ping`) and anything that is not JSON are ignored rather than raised — a
 * heartbeat is not an error, and a stream that dies on one unreadable line would lose a whole run
 * over a keep-alive.
 */
export const parseFrame = (frame: string): { id: number; event: AnalysisEvent } | null => {
  let id = 0;
  const data: string[] = [];

  frame.split("\n").forEach((line) => {
    if (line.startsWith(":")) return;
    if (line.startsWith("id:")) id = parseInt(line.slice(3).trim(), 10);
    if (line.startsWith("data:")) data.push(line.slice(5).trim());
  });

  if (data.length === 0) return null;

  try {
    return { id: Number.isNaN(id) ? 0 : id, event: JSON.parse(data.join("\n")) as AnalysisEvent };
  } catch {
    return null;
  }
};
